import { Modal } from '@shared/components';
import { Option, Select, TextArea } from '@shared/components/react-hook-form';
import { MessageRatingReason } from '@shared/generated/graphql';
import { ReactElement, RefObject } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { MessageRatingDetails } from '../../hooks/useMessageFeedback';

const REASON_OPTIONS: { value: MessageRatingReason; label: string }[] = [
  {
    value: MessageRatingReason.DIDNT_DO_WHAT_I_ASKED,
    label: "Didn't do what I asked",
  },
  { value: MessageRatingReason.WRONG_INFORMATION, label: 'Wrong information' },
  {
    value: MessageRatingReason.TOOK_THE_WRONG_ACTION,
    label: 'Took the wrong action',
  },
  { value: MessageRatingReason.INCOMPLETE_ANSWER, label: 'Incomplete answer' },
  { value: MessageRatingReason.ERROR_OR_BUG, label: 'Error or bug' },
  { value: MessageRatingReason.OTHER, label: 'Other' },
];

const MAX_DETAILS_LENGTH = 5000;

type MessageFeedbackModalProps = {
  isOpen: boolean;
  isPositive: boolean;
  isSubmitting: boolean;
  returnFocusRef: RefObject<HTMLElement | null>;
  onClose: () => void;
  onSubmit: (details: MessageRatingDetails) => Promise<void>;
};

export function MessageFeedbackModal({
  isOpen,
  isPositive,
  isSubmitting,
  returnFocusRef,
  onClose,
  onSubmit,
}: MessageFeedbackModalProps): ReactElement {
  return (
    <Modal.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !isSubmitting) onClose();
      }}
    >
      {/* NOTE(aman): The issue menu isn't portaled, so it has to overflow
          the card. */}
      <Modal.Content
        size="medium"
        aria-describedby={undefined}
        className="!overflow-visible p-5"
        // NOTE(aman): The thumbs aren't a Radix trigger, so without this focus
        // falls to the page when the dialog closes.
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          returnFocusRef.current?.focus();
        }}
      >
        <MessageFeedbackForm
          isPositive={isPositive}
          isSubmitting={isSubmitting}
          onSubmit={onSubmit}
        />
      </Modal.Content>
    </Modal.Root>
  );
}

function MessageFeedbackForm({
  isPositive,
  isSubmitting,
  onSubmit,
}: Pick<
  MessageFeedbackModalProps,
  'isPositive' | 'isSubmitting' | 'onSubmit'
>) {
  const methods = useForm<MessageRatingDetails>({
    defaultValues: { reason: null, comments: '' },
  });
  const submitForm = methods.handleSubmit(onSubmit);

  return (
    <FormProvider {...methods}>
      <form onSubmit={submitForm} className="flex flex-col gap-3">
        <Modal.Title>
          {isPositive ? 'Give positive feedback' : 'Give negative feedback'}
        </Modal.Title>
        {!isPositive && (
          <Select<MessageRatingReason | null, MessageRatingDetails>
            control={methods.control}
            name="reason"
            label="What type of issue do you wish to report? (optional)"
            renderValue={(value) =>
              REASON_OPTIONS.find((option) => option.value === value)?.label
            }
          >
            {REASON_OPTIONS.map(({ value, label }) => (
              <Option key={value} value={value}>
                {label}
              </Option>
            ))}
          </Select>
        )}
        <TextArea<MessageRatingDetails>
          control={methods.control}
          name="comments"
          label="Please provide details: (optional)"
          rows={3}
          rules={{
            maxLength: {
              value: MAX_DETAILS_LENGTH,
              message: 'Keep details under 5,000 characters.',
            },
          }}
          placeholder={
            isPositive
              ? 'What was satisfying about this response?'
              : 'What was unsatisfying about this response?'
          }
        />
        <div className="flex justify-end gap-2">
          <Modal.FooterButton
            isCloseButton
            variant="secondary"
            isDisabled={isSubmitting}
          >
            Cancel
          </Modal.FooterButton>
          <Modal.FooterButton
            isPrimary
            onPress={() => void submitForm()}
            isDisabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </Modal.FooterButton>
        </div>
      </form>
    </FormProvider>
  );
}
